import { useMemo, useState } from 'react';
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	FormGroup,
	Input,
	Label,
	Alert,
} from 'reactstrap';
import { Btn } from 'AbstractElements';
import { useDispatch, useSelector } from 'react-redux';
import { exportToolLeads } from 'Features/Learning/ToolLeads/Utils/ToolLeadUtils';
import { EXPORT_COLUMNS, TOOL_LABELS } from './ToolLeadUtils';

const ModalExportToolLead = ({ isOpen, toggle }) => {
	const dispatch = useDispatch();
	const filters = useSelector((state) => state.ToolLead.filters);
	const selectedIds = useSelector((state) => state.ToolLead.selectedIds);
	const pagination = useSelector((state) => state.ToolLead.data?.pagination);
	const isExport = useSelector((state) => state.ToolLead.isExport);

	const defaultColumns = EXPORT_COLUMNS.filter((col) => col.defaultChecked).map(
		(col) => col.key
	);
	const [selectedColumns, setSelectedColumns] = useState(defaultColumns);
	const [format, setFormat] = useState('xlsx');
	const [onlySelected, setOnlySelected] = useState(false);

	const filterSummary = useMemo(() => {
		const parts = [];
		if (filters['filter[tools_name]']) {
			const labels = String(filters['filter[tools_name]'])
				.split(',')
				.map((slug) => TOOL_LABELS[slug] || slug)
				.join(', ');
			parts.push(`Tools: ${labels}`);
		}
		if (filters['filter[name]']) parts.push(`Nama: ${filters['filter[name]']}`);
		if (filters['filter[email]']) parts.push(`Email: ${filters['filter[email]']}`);
		if (filters['filter[whatsapp]']) parts.push(`WA: ${filters['filter[whatsapp]']}`);
		if (filters['filter[date_from]'] || filters['filter[date_to]']) {
			parts.push(
				`Tanggal: ${filters['filter[date_from]'] || '...'} s/d ${filters['filter[date_to]'] || '...'}`
			);
		}
		return parts.length ? parts.join(' · ') : 'Tanpa filter tambahan';
	}, [filters]);

	const toggleColumn = (key) => {
		setSelectedColumns((prev) =>
			prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
		);
	};

	const handleSelectAll = () => {
		setSelectedColumns(EXPORT_COLUMNS.map((col) => col.key));
	};

	const handleResetColumns = () => {
		setSelectedColumns(defaultColumns);
	};

	const handleExport = (e) => {
		e.preventDefault();
		if (!selectedColumns.length) {
			return;
		}

		const { page, per_page, include, ...activeFilters } = filters;
		const params = {
			...activeFilters,
			format,
			columns: selectedColumns,
		};

		if (onlySelected && selectedIds.length > 0) {
			params.ids = selectedIds.slice(0, 1000);
		}

		dispatch(exportToolLeads(params)).then((action) => {
			if (exportToolLeads.fulfilled.match(action)) {
				toggle();
			}
		});
	};

	return (
		<Modal isOpen={isOpen} toggle={toggle} size="lg">
			<ModalHeader toggle={toggle}>Export Tool Leads</ModalHeader>
			<ModalBody>
				<Alert color="info" className="mb-3">
					<div>
						<strong>Filter aktif:</strong> {filterSummary}
					</div>
					<div>
						<strong>Total baris (filter saat ini):</strong>{' '}
						{pagination?.total ?? 0}
					</div>
					{onlySelected && (
						<div>
							<strong>Baris terpilih:</strong> {selectedIds.length}
						</div>
					)}
				</Alert>

				<div className="d-flex gap-2 mb-3">
					<Btn attrBtn={{ color: 'light', size: 'sm', onClick: handleSelectAll }}>
						Pilih semua
					</Btn>
					<Btn attrBtn={{ color: 'light', size: 'sm', onClick: handleResetColumns }}>
						Reset kolom
					</Btn>
				</div>

				<div className="row">
					{EXPORT_COLUMNS.map((col) => (
						<div className="col-md-6" key={col.key}>
							<FormGroup check className="mb-2">
								<Input
									id={`export-col-${col.key}`}
									type="checkbox"
									checked={selectedColumns.includes(col.key)}
									onChange={() => toggleColumn(col.key)}
								/>
								<Label check htmlFor={`export-col-${col.key}`}>
									{col.label}
								</Label>
							</FormGroup>
						</div>
					))}
				</div>

				<hr />

				<FormGroup>
					<Label for="export-format">Format</Label>
					<Input
						id="export-format"
						type="select"
						value={format}
						onChange={(e) => setFormat(e.target.value)}
					>
						<option value="xlsx">XLSX</option>
						<option value="csv">CSV</option>
					</Input>
				</FormGroup>

				<FormGroup check>
					<Input
						id="export-only-selected"
						type="checkbox"
						checked={onlySelected}
						disabled={!selectedIds.length}
						onChange={(e) => setOnlySelected(e.target.checked)}
					/>
					<Label check htmlFor="export-only-selected">
						Hanya baris terpilih ({selectedIds.length})
					</Label>
				</FormGroup>
			</ModalBody>
			<ModalFooter>
				<Btn attrBtn={{ color: 'secondary', onClick: toggle, disabled: isExport }}>
					Batal
				</Btn>
				<Btn
					attrBtn={{
						color: 'warning',
						onClick: handleExport,
						disabled: isExport || !selectedColumns.length,
					}}
				>
					{isExport ? 'Mengunduh...' : 'Unduh'}
				</Btn>
			</ModalFooter>
		</Modal>
	);
};

export default ModalExportToolLead;
